import { FC, Fragment, useContext, useEffect, useRef, useState } from "react";
import {
  CircularProgress,
  MenuItem,
  Select,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  SxProps,
  Tooltip,
  Box,
  Typography,
  Collapse,
} from "@mui/material";
import { FiArrowUpCircle, FiRefreshCw, FiCheck } from "react-icons/fi";
import { RiSparkling2Fill } from "react-icons/ri";
import { useTranslation } from "react-i18next";

import {
  api,
  DynamicAllocationTier,
  IKmosV2TimeSlotDateAllocationsResponse,
  KmosAllocation,
  KmosAllocationType,
  LaneAllocationSystem
} from "../../../logic/api";
import styles from "./KmosView.module.css";
import KmosDialogRemoveAllocation from "./KmosDialogRemoveAllocation";
import { SessionContext } from "../../../utility/useSession";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";
import { usePreferences } from "../../../utility/usePreferences";
import { red } from "@mui/material/colors";

const KmosDialogLanes: FC<{
  timeSlotAllocations: IKmosV2TimeSlotDateAllocationsResponse[] | null;
  onChange: () => void;
}> = ({ timeSlotAllocations, onChange }) => {
  const closeAllocationAPI = api.hooks.kmosV2.closeAllocation();
  const openAllocationAPI = api.hooks.kmosV2.openAllocation();
  const prioritiseAllocationAPI = api.hooks.kmosV2.prioritiseAllocation();
  const actionSuggestedAllocationAPI = api.hooks.kmosV2.actionSuggestedAllocation();
  const session = useContext(SessionContext);
  const [saving, setSaving] = useState<boolean>(false);
  const scrollContainerRef = useRef(null);
  const timeSlotAllocationsPreviousLength = useRef(0);
  const { kmosManualAllocationEnabled } = usePreferences();
  const kmosManualAllocationReleased = useReleaseManagement(ReleaseFlag.kmosManualAllocationReleased);
  const { t } = useTranslation();
  const [displaySuggestedRemovals, setDisplaySuggestedRemovals] = useState(-1);

  const getAllocationRow = (
    allocationData: IKmosV2TimeSlotDateAllocationsResponse,
    priority?: number
  ) => {
    const isNotMainActiveAllocation =
      allocationData.allocation.active && priority && priority > 1;
    const isDASAllocation = allocationData.allocation.allocatedBy?.userName === 'dynamic.allocation';
    const tableCellStyle: SxProps = {
      p: 0,
      color:
        !allocationData.allocation.active
          ? '#757575'
          : 'initial',
    }

    return (
      <TableRow
        key={allocationData.allocation.id}
        sx={{
          position: 'relative',
          '&::after': isDASAllocation ? {
            content: '""',
            width: 4,
            background: 'linear-gradient(135deg, #FFD3A5 10%, #FD6585 100%);',
            position: 'absolute',
            bottom: 0,
            left: 0,
            top: 0,
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
          } : {},
          background:
            !allocationData.allocation.active
              ? '#eee'
              : 'initial',
        }}>
        <TableCell sx={tableCellStyle} align="right">
          {
            isDASAllocation && (
              <Tooltip title={t('kmos.labels.dasAllocationTooltip')}>
                <IconButton size="small" sx={{ color: '#FD6585' }}>
                  <RiSparkling2Fill size={16} />
                </IconButton>
              </Tooltip>
            )
          }
          <IconButton
            sx={{ color: 'inherit' }}
            size="small"
            onClick={() => prioritiseAllocation(allocationData.allocation)}
            disabled={!isNotMainActiveAllocation}
          >
            <FiArrowUpCircle size={16} />
          </IconButton>
        </TableCell>
        <TableCell sx={tableCellStyle} align="center">
          {priority || ""}
        </TableCell>
        <TableCell sx={tableCellStyle}>
          {allocationData.allocation.location.parentName}
        </TableCell>
        <TableCell sx={tableCellStyle}>
          {allocationData.allocation.location.name}
        </TableCell>
        <TableCell sx={tableCellStyle} align="center">
          {allocationData.allocation.location.capacity}
        </TableCell>
        <TableCell sx={tableCellStyle} align="center">
          {allocationData.metrics.arrived}
        </TableCell>
        <TableCell sx={tableCellStyle} align="center">
          {allocationData.metrics.parked}
        </TableCell>
        <TableCell sx={tableCellStyle} align="center">
          {allocationData.metrics.spaces}
        </TableCell>
        <TableCell sx={tableCellStyle}>
          <Select
            size="small"
            variant="standard"
            margin="dense"
            sx={{
              ...tableCellStyle,
              fontSize: 14,
            }}
            value={allocationData.allocation.active ? "1" : "0"}
            onChange={(event) => {
              if (event.target.value === "0") {
                closeAllocation(allocationData.allocation);
              } else {
                openAllocation(allocationData.allocation);
              }
            }}
          >
            <MenuItem value="1">{t("general.open")}</MenuItem>
            <MenuItem value="0">{t("general.closed")}</MenuItem>
          </Select>
        </TableCell>
        <TableCell sx={tableCellStyle} align="center">
          <KmosDialogRemoveAllocation
            allocation={allocationData.allocation}
            isDASAllocation={isDASAllocation}
            onRemoved={onChange}
          />
        </TableCell>
      </TableRow >
    );
  };

  const getAllocationId = (allocation: KmosAllocation) => ({
    timeSlotDate: {
      timeSlot: allocation.timeSlot,
      date: allocation.allocationDate,
    },
    locationID: allocation.location.id,
  });

  const closeAllocation = (allocation: KmosAllocation) => {
    closeAllocationAPI.send(
      session.current.context,
      getAllocationId(allocation),
      onChange
    );
  };

  const openAllocation = (allocation: KmosAllocation) => {
    openAllocationAPI.send(
      session.current.context,
      getAllocationId(allocation),
      onChange
    );
  };

  const prioritiseAllocation = (allocation: KmosAllocation) => {
    setSaving(true);
    const { id, ordinal } = allocation;
    prioritiseAllocationAPI.send(
      session.current.context,
      {
        allocationId: id,
        ordinal: ordinal - 1,
      },
      () => {
        onChange();
        setSaving(false);
      }
    );
  }

  const handleCreateSuggestedAllocationClick = (a: KmosAllocation) => {
    actionSuggestedAllocationAPI.send(session.current.context, {
      allocationId: getAllocationId(a)
    }, () => {
      onChange();
    })
  }

  useEffect(() => {
    const didIncrease = timeSlotAllocationsPreviousLength.current < (timeSlotAllocations?.length || 0);
    timeSlotAllocationsPreviousLength.current = timeSlotAllocations?.length || 0;

    if (!scrollContainerRef.current || !didIncrease) {
      return;
    }

    const scrollContainer = scrollContainerRef.current as HTMLDivElement;

    scrollContainer.scroll({
      top: scrollContainer.scrollHeight,
    });
  }, [timeSlotAllocations]);

  const headerCellStyle: SxProps = {
    fontWeight: 'bold',
    fontSize: 12,
    p: 0,
  }

  let allocationPriority = 0;

  const allocations: IKmosV2TimeSlotDateAllocationsResponse[] = [];
  const suggestedCreations: IKmosV2TimeSlotDateAllocationsResponse[] = [];
  const suggestedRemovals: IKmosV2TimeSlotDateAllocationsResponse[] = [];

  timeSlotAllocations?.forEach((a) => {
    if (a.allocation.type === KmosAllocationType.suggested) {
      // How we're currently detecting if DAS has submitted a "suggestion to remove an existing allocation"
      if (a.allocation.laneClosedDate) {
        suggestedRemovals.push(a);
      } else {
        suggestedCreations.push(a);
      }
    } else {
      allocations.push(a);
    }
  });
  allocations.sort(
    (a, b) => Number(a.allocation.active) - Number(b.allocation.active)
  );

  return (
    <>
      {
        (session.current.site?.preferences.lane_allocation_system === LaneAllocationSystem.DAS &&
          session.current.site?.preferences.dynamic_allocation_tier === DynamicAllocationTier.Basic) &&
        (
          <Box>
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
              color="#FD6585"
              sx={{
                background: 'linear-gradient(to right, #FD6585,#FFD3A5 50%);',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              <RiSparkling2Fill size={16} /> {t("kmos.labels.suggestedActions")}
            </Typography>
            {timeSlotAllocations === null ? (
              <CircularProgress style={{ margin: "0 auto", display: "block", marginBottom: 20 }} />
            ) : !suggestedCreations.length ? (
              <Typography gutterBottom color="GrayText">{t("kmos.labels.noSuggestedActions")}.</Typography>
            ) : (
              <TableContainer ref={scrollContainerRef} sx={{ position: 'relative', overflow: 'auto', maxHeight: 172 }}>
                <Table
                  size="small"
                  sx={{ tableLayout: 'fixed' }}
                  onMouseLeave={() => setDisplaySuggestedRemovals(-1)}
                >
                  <TableHead className={styles.allocationHeader}>
                    <TableRow>
                      <TableCell sx={headerCellStyle}>{t("input.labels.zone")}</TableCell>
                      <TableCell sx={headerCellStyle}>{t("kmos.labels.lane")}</TableCell>
                      <TableCell sx={headerCellStyle}>{t("kmos.labels.capacity")}</TableCell>
                      <TableCell sx={headerCellStyle}>{t("kmos.labels.allocated")}</TableCell>
                      <TableCell sx={headerCellStyle}>{t("kmos.labels.parked")}</TableCell>
                      <TableCell sx={headerCellStyle}>{t("kmos.labels.spaces")}</TableCell>
                      <TableCell sx={headerCellStyle} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suggestedCreations.map((a, i) => {
                      const sx = {
                        p: 0,
                        border: 0,
                      }
                      return (
                        <Fragment key={a.allocation.id}>
                          <TableRow
                            onMouseEnter={() => setDisplaySuggestedRemovals(i)}
                            sx={{
                              borderBottom: '1px solid lightgrey',
                              borderTop: 0,
                            }}
                          >
                            <TableCell sx={{ ...sx, pl: 1 }}>{a.allocation.location.parentName}</TableCell>
                            <TableCell sx={sx}>{a.allocation.location.name}</TableCell>
                            <TableCell sx={sx} align="center">{a.allocation.location.capacity}</TableCell>
                            <TableCell sx={sx} align="center">{a.metrics.arrived}</TableCell>
                            <TableCell sx={sx} align="center">{a.metrics.parked}</TableCell>
                            <TableCell sx={sx} align="center">{a.metrics.spaces}</TableCell>
                            <TableCell sx={sx} align="center">
                              <Tooltip title={t('kmos.labels.acceptSuggestion')}>
                                <IconButton size="small" color="success" onClick={() => {
                                  handleCreateSuggestedAllocationClick(a.allocation)
                                }}>
                                  <FiCheck size={16} />
                                </IconButton>
                              </Tooltip>
                              <KmosDialogRemoveAllocation
                                allocation={a.allocation}
                                isDASAllocation
                                onRemoved={onChange}
                              />
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ backgroundColor: red[50] }}>
                            <TableCell colSpan={7} sx={{ p: 0, border: 0, width: '100%' }}>
                              <Collapse in={displaySuggestedRemovals === i} timeout="auto" unmountOnExit>
                                <Box>
                                  <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                    <TableBody>
                                      {suggestedRemovals.map((removal, j) => (
                                        <TableRow
                                          key={removal.allocation.id}
                                          sx={{
                                            border: '1px solid',
                                            borderTop: 0,
                                            borderBottom: j === suggestedRemovals.length - 1 ? '1px solid' : 0,
                                            borderColor: red[100],
                                            backgroundColor: red[50],
                                            opacity: 0.7,
                                          }}
                                        >
                                          <TableCell sx={{ ...sx, pl: 1, py: 1 }}>{removal.allocation.location.parentName}</TableCell>
                                          <TableCell sx={sx}>{removal.allocation.location.name}</TableCell>
                                          <TableCell sx={sx} align="center">-{removal.allocation.location.capacity}</TableCell>
                                          <TableCell sx={sx} align="center">{removal.metrics.arrived}</TableCell>
                                          <TableCell sx={sx} align="center">{removal.metrics.parked}</TableCell>
                                          <TableCell sx={sx} align="center">-{removal.metrics.spaces}</TableCell>
                                          <TableCell sx={sx} align="left">
                                            <Typography fontFamily="monospace" fontSize="small">
                                              {t('kmos.labels.unallocate')}
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      <Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {t("kmos.labels.allocatedLanes")}
        </Typography>
        {allocations === null ? (
          <CircularProgress style={{ margin: "0 auto", display: "block", marginBottom: 20 }} />
        ) : !allocations.length ? (
          <Typography gutterBottom color="GrayText">{t("input.validations.noLanesAllocated")}.</Typography>
        ) : (
          <TableContainer ref={scrollContainerRef} sx={{ position: 'relative', overflow: 'auto', maxHeight: 172 }}>
            {saving && (
              <div className={styles.dialogAllocationLaneSaveOverlay}>
                <CircularProgress />
              </div>
            )}
            <Table size="small">
              <TableHead className={styles.allocationHeader}>
                <TableRow>
                  <TableCell sx={headerCellStyle}></TableCell>
                  <TableCell sx={headerCellStyle}>{t("kmos.labels.priority")}</TableCell>
                  <TableCell sx={headerCellStyle}>{t("input.labels.zone")}</TableCell>
                  <TableCell sx={headerCellStyle}>{t("kmos.labels.lane")}</TableCell>
                  <TableCell sx={headerCellStyle}>{t("kmos.labels.capacity")}</TableCell>
                  <TableCell sx={headerCellStyle}>{kmosManualAllocationEnabled == true && kmosManualAllocationReleased == true ? t("kmos.labels.allocated") : t("kmos.labels.arrived")}</TableCell>
                  <TableCell sx={headerCellStyle}>{t("kmos.labels.parked")}</TableCell>
                  <TableCell sx={headerCellStyle}>{t("kmos.labels.spaces")}</TableCell>
                  <TableCell sx={headerCellStyle}>{t("booking.cards.status")}</TableCell>
                  <TableCell sx={headerCellStyle}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  allocations.map((allocation) => {
                    if (allocation.allocation.active) {
                      allocationPriority += 1
                    }

                    return getAllocationRow(allocation, allocationPriority)
                  })
                }
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </>
  );
};

export default KmosDialogLanes;
